import React from 'react';

function RiskManagement() {
  return (
    <main className="bg-white">
      {/* Hero */}
      <section className="relative h-[56vh] min-h-[420px] overflow-hidden">
        <img src="/new-assets/pexels-mart-production-7709212.jpg" alt="Risk Management" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/60 to-black/80" />
        <div className="relative z-10 h-full flex items-center">
          <div className="max-w-7xl mx-auto px-6">
            <h1 className="text-white text-4xl sm:text-5xl md:text-6xl font-light tracking-[-0.02em]">Risk Management</h1>
            <p className="mt-4 text-white/90 max-w-2xl text-lg">Comprehensive risk assessment and protection strategies for your assets.</p>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-semibold text-gray-900">Protecting Your Wealth</h2>
            <p className="mt-3 text-gray-600 max-w-3xl mx-auto">Advanced portfolio risk analytics, hedging strategies, insurance solutions, and downside protection tailored to your risk tolerance.</p>
          </div>
          <div className="grid lg:grid-cols-2 gap-12 items-start mt-10">
            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-6">Holistic Risk Assessment</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Risk management is the foundation of sustainable wealth preservation. Our comprehensive approach identifies, measures, and mitigates risks across your entire financial picture—from market volatility and concentration risk to liquidity needs and longevity risk.
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                We employ sophisticated risk analytics including Value-at-Risk (VaR), stress testing, scenario analysis, and Monte Carlo simulations to quantify potential downside exposure. Our risk management framework integrates seamlessly with your investment strategy, ensuring your portfolio remains aligned with your risk tolerance through all market environments.
              </p>
              <div className="bg-orange-50 border-l-4 border-orange-600 p-5 rounded-r-lg">
                <p className="text-sm text-gray-700 leading-relaxed">
                  <strong className="text-orange-700">Risk Framework:</strong> Multi-layered approach combining portfolio diversification, hedging strategies, insurance solutions, and dynamic risk monitoring to protect capital and provide peace of mind.
                </p>
              </div>
            </div>
            <div className="relative w-full max-w-2xl mx-auto sm:mx-0">
              <img src="/new-assets/pexels-mart-production-7709260.jpg" alt="Risk management" className="w-full h-auto object-cover rounded-2xl shadow-xl" />
              <div className="absolute -bottom-5 -right-5 w-40 h-40 bg-orange-600 opacity-10 rounded-3xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Risk Management Services */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-semibold text-gray-900 text-center mb-10">Risk Management Services</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {title: 'Portfolio Risk Analytics', desc: 'Advanced quantitative analysis including volatility metrics, correlation analysis, drawdown scenarios, and tail risk assessment to understand portfolio risk exposures.'},
              {title: 'Hedging Strategies', desc: 'Sophisticated hedging techniques using options, futures, and alternative investments to protect against market downturns, currency risk, and concentrated positions.'},
              {title: 'Insurance Solutions', desc: 'Comprehensive review of life, disability, long-term care, property & casualty, and umbrella coverage to protect against catastrophic losses and ensure adequate protection.'}
            ].map((service, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Risk Types */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-semibold text-gray-900">Risk Categories We Address</h2>
            <p className="mt-3 text-gray-600 max-w-3xl mx-auto">Comprehensive coverage across all dimensions of financial risk.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {type: 'Market Risk', points: ['Equity volatility', 'Interest rate risk', 'Credit spread risk', 'Commodity exposure']},
              {type: 'Concentration Risk', points: ['Single-stock positions', 'Sector concentration', 'Geographic exposure', 'Manager concentration']},
              {type: 'Liquidity Risk', points: ['Illiquid assets', 'Redemption timing', 'Emergency reserves', 'Lock-up periods']},
              {type: 'Longevity Risk', points: ['Retirement income', 'Healthcare costs', 'Inflation protection', 'Spending sustainability']},
              {type: 'Event Risk', points: ['Disability income', 'Premature death', 'Liability claims', 'Business interruption']},
              {type: 'Regulatory Risk', points: ['Tax law changes', 'Estate tax planning', 'Compliance requirements', 'Policy impacts']}
            ].map((item, idx) => (
              <div key={idx} className="p-6 bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-100">
                <h4 className="font-semibold text-gray-900 mb-3">{item.type}</h4>
                <ul className="space-y-2">
                  {item.points.map((point, i) => (
                    <li key={i} className="text-sm text-gray-600 flex gap-2">
                      <span className="text-[#0056d7]">•</span> {point}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Risk Monitoring */}
      <section className="py-16 bg-gradient-to-br from-orange-900 to-orange-800 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-semibold">Continuous Risk Monitoring</h2>
            <p className="mt-3 text-white/80 max-w-3xl mx-auto">Real-time risk surveillance and proactive risk management.</p>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              {label: 'Daily Monitoring', desc: 'Real-time tracking of portfolio volatility, correlation changes, and risk metrics'},
              {label: 'Quarterly Reviews', desc: 'Comprehensive risk assessment reports with stress test results and recommendations'},
              {label: 'Alert System', desc: 'Automated alerts for significant risk threshold breaches or concentration buildups'},
              {label: 'Annual Planning', desc: 'Strategic risk review aligned with changing life circumstances and goals'}
            ].map((item, idx) => (
              <div key={idx} className="bg-white/10 backdrop-blur rounded-xl p-6 border border-white/20">
                <h4 className="font-semibold mb-2">{item.label}</h4>
                <p className="text-sm text-white/80">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h3 className="text-3xl font-semibold text-gray-900">Protect Your Wealth</h3>
          <p className="mt-2 text-gray-600">Schedule a comprehensive risk assessment with our risk management specialists.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <a href="/apply" className="px-6 py-3 rounded-md bg-gradient-to-r from-[#fbbf24] to-[#f59e0b] text-black font-semibold hover:shadow-lg transition-shadow">Get Started</a>
            <a href="/contact" className="px-6 py-3 rounded-md border-2 border-gray-300 text-gray-700 hover:border-gray-400 transition-colors">Contact Us</a>
          </div>
        </div>
      </section>
    </main>
  );
}

export default RiskManagement;

